import { Modal, Progress, Spin } from "antd";
import { useTranslation } from "react-i18next";
import imgAnnoDominiWeb from "shared/assets/images/anno-domini-logo-stage-bg.png";
import imgAnnoDominiLogo from "shared/assets/images/anno-domini-logo.png";
import "explorer/pages/new-estagio-artista/campanhas-especiais/CampanhasEspeciais.scss";
import { useSelector } from "react-redux";
import { isMobile } from "react-device-detect";
import { getSpecialCampaignEligibility } from "shared/service/StrmService";
import { LoadingOutlined } from "@ant-design/icons";
import { SpecialCampaignEnum } from "shared/enums/SpecialCampaignEnum";
import { useState, useEffect } from "react";

interface ModalCampaignADNotEligibleProps {
  open: boolean;
  onCancel: () => void;
}

enum MotivoNaoElegivel {
  Estagio = 0,
  AdiantamentoEmProgresso = 1,
  Genero = 2,
  Pais = 3,
  DistroEmProgresso = 4,
}

const ModalCampaignADNotEligible: React.FC<ModalCampaignADNotEligibleProps> = (
  props
) => {
  const { open, onCancel } = props;
  const { t } = useTranslation();
  const { userArtist } = useSelector((state: any) => state.artists);
  const { titulo: tituloEstagio } = t(
    `pages.EstagioArtista.estagio.${userArtist?.stage || 0}`
  ) as any;

  const [motivoNaoElegivel, setMotivoNaoElegivel] =
    useState<MotivoNaoElegivel | null>(null);
  const [loading, setLoading] = useState(false);

  const getTranslateReason = (reason: MotivoNaoElegivel | null): any => {
    if (reason == null) return {};

    let key = "";

    switch (reason) {
      case MotivoNaoElegivel.Estagio:
        key = "estagio";
        break;
      case MotivoNaoElegivel.AdiantamentoEmProgresso:
        key = "advance";
        break;
      case MotivoNaoElegivel.Genero:
        key = "genero";
        break;
      case MotivoNaoElegivel.Pais:
        key = "pais";
        break;
      case MotivoNaoElegivel.DistroEmProgresso:
        key = "distro";
        break;
      default:
        key = "estagio";
        break;
    }

    return t(`pages.CampanhasEspeciais.AnnoDomini.inelegivel.${key}`);
  };

  useEffect(() => {
    const fetchEligibility = async () => {
      setLoading(true);
      try {
        const eligibility: any = await getSpecialCampaignEligibility(
          SpecialCampaignEnum.AnnoDomini
        );

        if (eligibility?.response?.ineligibleReason !== undefined) {
          setMotivoNaoElegivel(
            eligibility.response.ineligibleReason as MotivoNaoElegivel
          );
        }
      } catch (error) {
        console.error("Error fetching eligibility:", error);
        setMotivoNaoElegivel(MotivoNaoElegivel.Genero);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchEligibility();
    }
  }, [open]);

  const { topo, titulo, descricao } = getTranslateReason(motivoNaoElegivel);
  const beneficios: string[] = t(
    "pages.CampanhasEspeciais.AnnoDomini.inelegivel.beneficios"
  );

  return (
    <Modal
      open={open}
      footer={null}
      onCancel={onCancel}
      width={700}
      title={null}
      className="modal-campanha-especial not-eligible"
      closable={true}
      maskClosable={false}
    >
      <>
        <div style={{ position: "relative" }}>
          <div
            className={`conteudo conteudo-anno-domini ${
              isMobile ? "mobile" : "web"
            }`}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              background: `linear-gradient(180deg, rgba(25, 25, 25, 0.3) 0%, #1A1A1A 50%), url(${imgAnnoDominiWeb}) no-repeat top center`,
              backgroundSize: "cover",
              borderRadius: "8px 8px 0 0",
            }}
          >
            {loading ? (
              <div>
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{ fontSize: 20, margin: 32 }}
                      spin={false}
                    />
                  }
                />
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={imgAnnoDominiLogo}
                    alt="Anno Domini Logo"
                    className="anno-domini-logo"
                    style={{
                      opacity: 0.5,
                      position: "absolute",
                      top: 0,
                    }}
                  />
                </div>
                <div
                  style={{
                    paddingTop: 112,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "0 48px",
                    zIndex: 1,
                  }}
                >
                  <>
                    {motivoNaoElegivel === MotivoNaoElegivel.Estagio && (
                      <div>
                        <Progress
                          type="circle"
                          percent={userArtist?.stagePercentual}
                          width={110}
                          strokeColor="#00D88A"
                          trailColor="#004129"
                          format={(value) => (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <span style={{ fontSize: 36, color: "#f2f2f2" }}>
                                {userArtist?.stage}
                              </span>
                              <span
                                style={{
                                  fontSize: 14,
                                  width: 80,
                                  color: "#f2f2f2",
                                }}
                              >
                                {tituloEstagio}
                              </span>
                              <span style={{ fontSize: 11, color: "#00D88A" }}>
                                {value?.toFixed(0)}%
                              </span>
                            </div>
                          )}
                        />
                      </div>
                    )}
                    <span className="chamada">{topo}</span>
                    <h1 className="titulo">{titulo}</h1>

                    <span
                      className="divider"
                      style={{ backgroundColor: "#474747" }}
                    />
                  </>
                </div>

                <p
                  style={{
                    padding: "0 48px",
                    textAlign: "center",
                  }}
                >
                  {descricao}
                </p>

                <div style={{ paddingTop: 12 }}>
                  <ul>
                    {beneficios?.map((beneficio: string, index: number) => (
                      <li
                        className="circle-checkmark azul"
                        key={`beneficio-${index}`}
                      >
                        {beneficio}
                      </li>
                    ))}
                  </ul>
                  <div className="buttons">
                    <button className="botao-roxo" onClick={onCancel}>
                      {t(
                        "pages.CampanhasEspeciais.AnnoDomini.inelegivel.continuarEvolucao"
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </>
    </Modal>
  );
};

export default ModalCampaignADNotEligible;
